import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { waypointsToMapTrack, normalizeMapTrack } from '../../utils/tripGpsResolve';
import {
  convertSpeed,
  getSpeedUnit,
  convertTemperature,
  getTemperatureUnit,
  formatCombinedFuelRate,
  formatFuelRate,
  fuelRateToLitersPerHour,
  resolveTripPreferenceUnits,
} from '../../utils/unitConversions';
import { getMarineChartRegion } from '../chartplotter/marineChartRegion';
import {
  MapInvalidateSize,
  STREET_MAP_ATTRIBUTION,
  CHART_MAX_ZOOM,
} from '../chartplotter/marineMapLayers';

// Fix for default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const INLAND_MAP_URL = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
const INLAND_MAP_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>';

const MapWrapper = styled.div`
  width: 100%;
  height: ${props => props.height || '500px'};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${theme.colors.border.primary};
  position: relative;
  
  .leaflet-container {
    height: 100%;
    width: 100%;
    background: #0f172a;
  }

  .leaflet-div-icon.trip-waypoint-icon,
  .custom-div-icon {
    background: transparent !important;
    border: none !important;
  }
  
  .leaflet-popup-content-wrapper {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
    border-radius: ${theme.borderRadius.md};
    border: 1px solid ${theme.colors.border.primary};
  }
  
  .leaflet-popup-tip {
    background: ${theme.colors.background.card};
    border: 1px solid ${theme.colors.border.primary};
  }
  
  .leaflet-control-layers {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.primary};
  }
  
  .leaflet-control-layers-toggle {
    background-color: ${theme.colors.background.card};
    border: 1px solid ${theme.colors.border.primary};
  }
  
  .leaflet-control-zoom a {
    background-color: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.primary};
    
    &:hover {
      background-color: ${theme.colors.background.tertiary};
    }
  }

  .trip-route-segment {
    cursor: crosshair;
  }
`;

/** Semi-transparent banner when route data is missing — map tiles still visible underneath. */
const NoRouteBanner = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 23, 42, 0.88);
  padding: 0.75rem 1.25rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.primary};
  z-index: 1000;
  text-align: center;
  font-size: 0.875rem;
  pointer-events: none;
`;

const MapStats = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: rgba(15, 23, 42, 0.92);
  padding: 0.75rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.primary};
  z-index: 1000;
  font-size: 0.875rem;
  display: flex;
  gap: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  
  span {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: ${theme.colors.text.secondary};
    
    strong {
      color: ${theme.colors.text.primary};
      font-weight: 700;
    }
  }
`;

const ROUTE_HOVER_HIT_PX = 18;

/** Pixel distance from point p to line segment a–b (Leaflet container points). */
function distancePxToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return p.distanceTo(a);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)));
  return p.distanceTo(L.point(a.x + t * dx, a.y + t * dy));
}

/** Hover along the route → nearest track point for telemetry popup. */
const MapTrackHover = ({ trackPoints, onHoverIndex }) => {
  const map = useMap();
  const rafRef = useRef(null);
  const lastIdxRef = useRef(-1);

  useEffect(() => {
    if (!trackPoints?.length || !onHoverIndex) return undefined;

    const findHoverIndex = (latlng) => {
      const cursor = map.latLngToContainerPoint(latlng);
      let bestIdx = -1;
      let bestDist = ROUTE_HOVER_HIT_PX;

      if (trackPoints.length === 1) {
        const point = trackPoints[0];
        const lat = parseFloat(point.latitude);
        const lon = parseFloat(point.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          const pt = map.latLngToContainerPoint([lat, lon]);
          if (cursor.distanceTo(pt) <= ROUTE_HOVER_HIT_PX) return 0;
        }
        return -1;
      }

      for (let i = 0; i < trackPoints.length - 1; i += 1) {
        const lat1 = parseFloat(trackPoints[i].latitude);
        const lon1 = parseFloat(trackPoints[i].longitude);
        const lat2 = parseFloat(trackPoints[i + 1].latitude);
        const lon2 = parseFloat(trackPoints[i + 1].longitude);
        if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) continue;

        const p1 = map.latLngToContainerPoint([lat1, lon1]);
        const p2 = map.latLngToContainerPoint([lat2, lon2]);
        const segDist = distancePxToSegment(cursor, p1, p2);
        if (segDist < bestDist) {
          bestDist = segDist;
          bestIdx = cursor.distanceTo(p1) <= cursor.distanceTo(p2) ? i : i + 1;
        }
      }

      return bestIdx;
    };

    const onMove = (e) => {
      if (map.dragging?.moving()) return;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const idx = findHoverIndex(e.latlng);
        if (idx !== lastIdxRef.current) {
          lastIdxRef.current = idx;
          onHoverIndex(idx >= 0 ? idx : null);
        }
      });
    };

    const onLeave = () => {
      lastIdxRef.current = -1;
      onHoverIndex(null);
    };

    map.on('mousemove', onMove);
    map.on('mouseout', onLeave);
    return () => {
      map.off('mousemove', onMove);
      map.off('mouseout', onLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [map, trackPoints, onHoverIndex]);

  return null;
};

/** Popup anchor at a track point — opens automatically when mounted or index changes. */
function TripTrackPopupMarker({ point, index, emphasized, children }) {
  const markerRef = useRef(null);
  const lat = parseFloat(point.latitude);
  const lon = parseFloat(point.longitude);

  useEffect(() => {
    const timer = setTimeout(() => markerRef.current?.openPopup(), 0);
    return () => clearTimeout(timer);
  }, [lat, lon, index]);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  return (
    <Marker
      ref={markerRef}
      position={[lat, lon]}
      icon={L.divIcon({
        className: 'trip-track-popup-anchor',
        html: `<div style="
          width:${emphasized ? 16 : 12}px;
          height:${emphasized ? 16 : 12}px;
          border-radius:50%;
          background:${emphasized ? '#fff' : 'rgba(255,255,255,0.92)'};
          border:${emphasized ? '3px solid #3b82f6' : '2px solid #3b82f6'};
          box-shadow:0 2px 8px rgba(0,0,0,0.35);
          pointer-events:none;
        "></div>`,
        iconSize: [emphasized ? 16 : 12, emphasized ? 16 : 12],
        iconAnchor: [emphasized ? 8 : 6, emphasized ? 8 : 6],
      })}
      zIndexOffset={2000}
      interactive={false}
    >
      <Popup closeButton autoPan minWidth={220}>
        {children}
      </Popup>
    </Marker>
  );
}

// Custom marker icons
const createNumberedIcon = (number, color = '#f59e0b', selected = false) => {
  const size = selected ? 36 : 32;
  return L.divIcon({
    className: 'custom-div-icon trip-waypoint-icon',
    html: `
      <div style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: white;
        font-size: 14px;
        cursor: pointer;
        pointer-events: auto;
      ">${number}</div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -16],
  });
};

function TripWaypointMarker({ waypoint, index, selected, onSelect, formatDate }) {
  const markerRef = useRef(null);
  const lat = parseFloat(waypoint.latitude);
  const lon = parseFloat(waypoint.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  return (
    <Marker
      ref={markerRef}
      position={[lat, lon]}
      icon={createNumberedIcon(index + 1, selected ? '#fbbf24' : '#f59e0b', selected)}
      zIndexOffset={selected ? 1000 : 500}
      eventHandlers={{
        click: (e) => {
          L.DomEvent.stopPropagation(e);
          markerRef.current?.openPopup();
          onSelect?.(index);
        },
      }}
    >
      <Popup>
        <div style={{ padding: '0.5rem', minWidth: '200px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            ⚓ {waypoint.waypoint_name || `Stop ${index + 1}`}
          </div>
          {waypoint.location_name && (
            <div style={{ marginBottom: '0.25rem' }}>📍 {waypoint.location_name}</div>
          )}
          {waypoint.arrival_time && (
            <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
              Arrived: {formatDate(waypoint.arrival_time)}
            </div>
          )}
          {waypoint.departure_time && (
            <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
              Departed: {formatDate(waypoint.departure_time)}
            </div>
          )}
          {waypoint.water_depth_meters && (
            <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
              🌊 Depth: {waypoint.water_depth_meters.toFixed(1)}m
            </div>
          )}
          {waypoint.notes && (
            <div style={{ fontSize: '0.875rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
              {waypoint.notes}
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

const startIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="#10b981" stroke="white" stroke-width="3"/>
      <path d="M12 16 L18 12 L18 20 Z" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const endIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="#ef4444" stroke="white" stroke-width="3"/>
      <rect x="11" y="11" width="10" height="10" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Component to fit map bounds to track
const FitBounds = ({ track, fallbackCenter }) => {
  const map = useMap();
  
  useEffect(() => {
    if (track && track.length > 0) {
      const bounds = L.latLngBounds(track);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      setTimeout(() => map.invalidateSize(), 50);
    } else if (fallbackCenter) {
      map.setView(fallbackCenter, 11);
      setTimeout(() => map.invalidateSize(), 50);
    }
  }, [track, fallbackCenter, map]);
  
  return null;
};

/** Click map or route line → nearest GPS point (works with 10k+ points without 10k markers). */
const MapNearestPointClick = ({ trackPoints, onPointSelect }) => {
  const map = useMap();

  useEffect(() => {
    if (!onPointSelect || !trackPoints?.length) return undefined;

    const findNearest = (lat, lng) => {
      let bestIdx = -1;
      let bestDist = Infinity;
      trackPoints.forEach((point, index) => {
        const plat = parseFloat(point.latitude);
        const plng = parseFloat(point.longitude);
        if (!Number.isFinite(plat) || !Number.isFinite(plng)) return;
        const d = (plat - lat) ** 2 + (plng - lng) ** 2;
        if (d < bestDist) {
          bestDist = d;
          bestIdx = index;
        }
      });
      return bestIdx;
    };

    const onMapClick = (e) => {
      const idx = findNearest(e.latlng.lat, e.latlng.lng);
      if (idx >= 0) onPointSelect(idx);
    };

    map.on('click', onMapClick);
    return () => {
      map.off('click', onMapClick);
    };
  }, [map, trackPoints, onPointSelect]);

  return null;
};

const TripMap = ({ 
  track = [], 
  trackPoints = [],
  waypoints = [], 
  startPoint, 
  endPoint, 
  height = '500px',
  showStats = true,
  telemetryView = 'speed',
  selectedIndex = null,
  selectedWaypointIndex = null,
  onPointSelect = null,
  onWaypointSelect = null,
  getPointColor = null,
  preferenceUnits = null,
}) => {
  const prefUnits = resolveTripPreferenceUnits(preferenceUnits || {});
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ points: 0, distance: 0 });
  const [hoverIndex, setHoverIndex] = useState(null);
  const handleHoverIndex = useCallback((index) => setHoverIndex(index), []);
  const popupIndex = hoverIndex ?? selectedIndex;

  const waypointTrack = waypointsToMapTrack(waypoints);
  const effectiveTrack = normalizeMapTrack(track && track.length > 0 ? track : waypointTrack);

  useEffect(() => {
    // Calculate statistics
    if (effectiveTrack && effectiveTrack.length > 0) {
      setStats({
        points: effectiveTrack.length,
        distance: calculateDistance(effectiveTrack)
      });
    }
    setIsLoading(false);
  }, [effectiveTrack]);

  const calculateDistance = (points) => {
    if (!points || points.length < 2) return 0;
    
    let totalDistance = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const [lat1, lon1] = points[i];
      const [lat2, lon2] = points[i + 1];
      totalDistance += getDistanceBetweenPoints(lat1, lon1, lat2, lon2);
    }
    return totalDistance;
  };

  const getDistanceBetweenPoints = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Default center — prefer track, then start point, then first waypoint
  const fallbackCenter = (() => {
    if (effectiveTrack && effectiveTrack.length > 0) return effectiveTrack[0];
    if (startPoint?.lat && startPoint?.lon) return [startPoint.lat, startPoint.lon];
    if (waypointTrack.length > 0) return waypointTrack[0];
    return [44.35, -78.72]; // Lakefield / Kawarthas fallback
  })();
  const defaultCenter = fallbackCenter;
  const chartRegion = getMarineChartRegion(defaultCenter[0], defaultCenter[1]);
  const useInlandBase = chartRegion === 'canada_inland';
  const hasRoute = effectiveTrack && effectiveTrack.length > 0;
  const pointColor = (point, index) => {
    if (typeof getPointColor === 'function') return getPointColor(point, index);
    return '#3b82f6';
  };

  const segmentPositions = effectiveTrack && effectiveTrack.length > 1 ? effectiveTrack : [];
  const segments = [];
  for (let i = 0; i < segmentPositions.length - 1; i += 1) {
    const point = trackPoints[i] || {};
    segments.push({
      positions: [segmentPositions[i], segmentPositions[i + 1]],
      color: pointColor(point, i),
    });
  }

  const formatPopupTelemetry = (point, index) => {
    if (!point) return null;
    const time = point.recorded_at
      ? new Date(point.recorded_at).toLocaleString()
      : '—';
    const rawKnots = Number(point.speed_knots);
    const speed = Number.isFinite(rawKnots)
      ? `${convertSpeed(rawKnots, prefUnits.speed_unit).toFixed(1)} ${getSpeedUnit(prefUnits.speed_unit)}`
      : '—';
    const depth = point.depth_meters != null ? `${Number(point.depth_meters).toFixed(1)} m` : '—';
    const heading = point.heading != null ? `${Number(point.heading).toFixed(0)}°` : '—';

    const formatLoad = (raw) => {
      const n = Number(raw);
      if (!Number.isFinite(n)) return '—';
      return `${(n <= 1 ? n * 100 : n).toFixed(0)}%`;
    };
    const formatRpm = (raw) => {
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? Math.round(n).toString() : '—';
    };
    const formatTemp = (raw) => {
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return '—';
      return `${convertTemperature(n, prefUnits.temperature_unit).toFixed(0)}${getTemperatureUnit(prefUnits.temperature_unit)}`;
    };

    const portFuelLph = fuelRateToLitersPerHour(point.fuel_rate_port);
    const stbdFuelLph = fuelRateToLitersPerHour(point.fuel_rate_starboard);
    const combinedFuel = formatCombinedFuelRate(
      point.fuel_rate_port,
      point.fuel_rate_starboard,
      prefUnits.fuel_unit,
      1
    );

    const rowStyle = { fontSize: '0.8rem', lineHeight: 1.55, marginBottom: '0.2rem' };
    const labelStyle = { color: theme.colors.text.secondary, marginRight: '0.35rem' };

    return (
      <div style={{ padding: '0.35rem', minWidth: '220px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.45rem' }}>
          Point #{index + 1}
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Time</span>
          {time}
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Position</span>
          {Number(point.latitude).toFixed(5)}°, {Number(point.longitude).toFixed(5)}°
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>SOG</span>
          {speed}
          <span style={labelStyle}> · Heading</span>
          {heading}
          <span style={labelStyle}> · Depth</span>
          {depth}
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>RPM</span>
          Port {formatRpm(point.rpm_port)} · Stbd {formatRpm(point.rpm_starboard)}
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Temp</span>
          Port {formatTemp(point.temp_port)} · Stbd {formatTemp(point.temp_starboard)}
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Load</span>
          Port {formatLoad(point.load_port)} · Stbd {formatLoad(point.load_starboard)}
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Fuel</span>
          {combinedFuel !== '—' ? (
            <>
              {combinedFuel} combined
              {(portFuelLph > 0 || stbdFuelLph > 0) ? (
                <div style={{ marginTop: '0.15rem', paddingLeft: '2.5rem', color: theme.colors.text.secondary }}>
                  Port {portFuelLph ? formatFuelRate(portFuelLph, prefUnits.fuel_unit, 1) : '—'}
                  {' · '}
                  Stbd {stbdFuelLph ? formatFuelRate(stbdFuelLph, prefUnits.fuel_unit, 1) : '—'}
                </div>
              ) : null}
            </>
          ) : '—'}
        </div>
      </div>
    );
  };
  
  if (!hasRoute && isLoading) {
    return (
      <MapWrapper height={height}>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: theme.colors.text.secondary,
        }}>
          Loading map…
        </div>
      </MapWrapper>
    );
  }

  return (
    <MapWrapper height={height}>
      <MapContainer
        center={defaultCenter}
        zoom={11}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <MapInvalidateSize />
        {useInlandBase ? (
          <TileLayer
            url={INLAND_MAP_URL}
            subdomains={['a', 'b', 'c', 'd']}
            attribution={INLAND_MAP_ATTRIBUTION}
            maxZoom={CHART_MAX_ZOOM}
            minZoom={2}
          />
        ) : (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            subdomains={['a', 'b', 'c']}
            attribution={STREET_MAP_ATTRIBUTION}
            maxZoom={CHART_MAX_ZOOM}
            minZoom={2}
          />
        )}

        <LayersControl position="topright" collapsed>
          <LayersControl.Overlay name="Buoys & Lights">
            <TileLayer
              url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
              attribution='&copy; <a href="http://www.openseamap.org">OpenSeaMap</a>'
              opacity={0.85}
            />
          </LayersControl.Overlay>
        </LayersControl>

        {/* GPS Track — colored segments paired with telemetry at each vertex */}
        {hasRoute && segments.length > 0 ? segments.map((seg, index) => (
          <Polyline
            key={`seg-${index}`}
            positions={seg.positions}
            pathOptions={{
              color: seg.color,
              weight: selectedIndex === index || selectedIndex === index + 1 ? 6 : 4,
              opacity: 0.9,
              lineCap: 'round',
              lineJoin: 'round',
              className: 'trip-route-segment',
            }}
            eventHandlers={onPointSelect ? {
              click: (e) => {
                L.DomEvent.stopPropagation(e);
                onPointSelect(index);
              },
              mouseover: (e) => {
                L.DomEvent.stopPropagation(e);
                const cursor = e.target._map.latLngToContainerPoint(e.latlng);
                const lat1 = parseFloat(trackPoints[index]?.latitude);
                const lon1 = parseFloat(trackPoints[index]?.longitude);
                const lat2 = parseFloat(trackPoints[index + 1]?.latitude);
                const lon2 = parseFloat(trackPoints[index + 1]?.longitude);
                if ([lat1, lon1, lat2, lon2].every(Number.isFinite)) {
                  const p1 = e.target._map.latLngToContainerPoint([lat1, lon1]);
                  const p2 = e.target._map.latLngToContainerPoint([lat2, lon2]);
                  handleHoverIndex(cursor.distanceTo(p1) <= cursor.distanceTo(p2) ? index : index + 1);
                } else {
                  handleHoverIndex(index);
                }
              },
            } : {
              mouseover: (e) => {
                L.DomEvent.stopPropagation(e);
                handleHoverIndex(index);
              },
            }}
          />
        )) : hasRoute && effectiveTrack && effectiveTrack.length > 1 && (
          <Polyline
            positions={effectiveTrack}
            pathOptions={{
              color: '#3b82f6',
              weight: 4,
              opacity: 0.8,
              lineCap: 'round',
              lineJoin: 'round',
            }}
          />
        )}

        {/* Sparse markers for inspection — click route or map for full resolution */}
        {trackPoints && trackPoints.length > 0 && trackPoints.length <= 800 && trackPoints.map((point, index) => {
          const lat = parseFloat(point.latitude);
          const lon = parseFloat(point.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
          const isSelected = selectedIndex === index;
          return (
            <Marker
              key={`tp-${index}`}
              position={[lat, lon]}
              icon={L.divIcon({
                className: 'trip-track-vertex',
                html: `<div style="
                  width:${isSelected ? 14 : 10}px;
                  height:${isSelected ? 14 : 10}px;
                  border-radius:50%;
                  background:${pointColor(point, index)};
                  border:2px solid ${isSelected ? '#fff' : 'rgba(255,255,255,0.7)'};
                  box-shadow:0 1px 4px rgba(0,0,0,0.35);
                "></div>`,
                iconSize: [isSelected ? 14 : 10, isSelected ? 14 : 10],
                iconAnchor: [isSelected ? 7 : 5, isSelected ? 7 : 5],
              })}
              eventHandlers={{
                click: () => onPointSelect?.(index),
                mouseover: () => handleHoverIndex(index),
              }}
            >
            </Marker>
          );
        })}

        {/* Telemetry popup for hover or selected track point */}
        {popupIndex != null && trackPoints[popupIndex] && (
          <TripTrackPopupMarker
            key={`popup-${popupIndex}`}
            point={trackPoints[popupIndex]}
            index={popupIndex}
            emphasized={selectedIndex === popupIndex}
          >
            {formatPopupTelemetry(trackPoints[popupIndex], popupIndex)}
          </TripTrackPopupMarker>
        )}

        {/* Start Marker */}
        {startPoint && startPoint.lat && startPoint.lon && (
          <Marker position={[startPoint.lat, startPoint.lon]} icon={startIcon}>
            <Popup>
              <div style={{ padding: '0.5rem' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#10b981' }}>
                  🚢 Trip Start
                </div>
                {startPoint.name && <div>{startPoint.name}</div>}
                {startPoint.time && <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                  {formatDate(startPoint.time)}
                </div>}
              </div>
            </Popup>
          </Marker>
        )}

        {/* Stop / waypoint markers */}
        {waypoints && waypoints.map((waypoint, index) => (
          <TripWaypointMarker
            key={waypoint.id || `wp-${index}`}
            waypoint={waypoint}
            index={index}
            selected={selectedWaypointIndex === index}
            onSelect={onWaypointSelect}
            formatDate={formatDate}
          />
        ))}

        {/* End Marker */}
        {endPoint && endPoint.lat && endPoint.lon && (
          <Marker position={[endPoint.lat, endPoint.lon]} icon={endIcon}>
            <Popup>
              <div style={{ padding: '0.5rem' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#ef4444' }}>
                  🏁 Trip End
                </div>
                {endPoint.name && <div>{endPoint.name}</div>}
                {endPoint.time && <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                  {formatDate(endPoint.time)}
                </div>}
              </div>
            </Popup>
          </Marker>
        )}

        <MapNearestPointClick trackPoints={trackPoints} onPointSelect={onPointSelect} />
        {trackPoints?.length > 0 && (
          <MapTrackHover trackPoints={trackPoints} onHoverIndex={handleHoverIndex} />
        )}
        <FitBounds track={hasRoute ? effectiveTrack : []} fallbackCenter={fallbackCenter} />
      </MapContainer>

      {!hasRoute && (
        <NoRouteBanner>
          No GPS route yet — use <strong>Repair trip from stops</strong> if this trip has anchor stops
        </NoRouteBanner>
      )}

      {showStats && stats.points > 0 && (
        <MapStats>
          <span>
            <strong>{stats.points}</strong> GPS points
          </span>
          <span>
            <strong>{stats.distance.toFixed(1)}</strong> km tracked
          </span>
          <span>
            <strong>{(stats.distance * 0.539957).toFixed(1)}</strong> nm
          </span>
        </MapStats>
      )}
    </MapWrapper>
  );
};

export default TripMap;

